import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Crown, Users, Building2, MessageCircle, Settings, ScrollText, UserPlus, Trash2, Edit3, Send, Mail, ShieldCheck } from "lucide-react";

const DEPARTMENTS = [
  { v: "finance", label: "Finance", color: "lime", desc: "Payments, refunds, payouts, GST" },
  { v: "operations", label: "Operations", color: "pink", desc: "Properties, KYC, vendors" },
  { v: "sales", label: "Sales", color: "blue", desc: "Leads, enquiries, conversions" },
  { v: "marketing", label: "Marketing", color: "lime", desc: "Content, campaigns, social" },
  { v: "support", label: "Support", color: "pink", desc: "Complaints, tickets, refund coord." },
  { v: "ground_staff", label: "Ground Staff", color: "white", desc: "Cooks, cleaners, security, maintenance" },
];
const GROUND_SUB = ["cook", "cleaner", "security", "maintenance"];

export default function SuperAdminPanel() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("staff");
  const [staff, setStaff] = useState([]);
  const [audit, setAudit] = useState([]);
  const [waLogs, setWaLogs] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [company, setCompany] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = async () => {
    api.get("/admin/staff").then(r => setStaff(r.data)).catch(()=>{});
    api.get("/admin/audit-logs").then(r => setAudit(r.data)).catch(()=>{});
    api.get("/whatsapp/logs").then(r => setWaLogs(r.data)).catch(()=>{});
    api.get("/admin/email-logs").then(r => setEmailLogs(r.data)).catch(()=>{});
    api.get("/admin/vendors").then(r => setVendors(r.data)).catch(()=>{});
    api.get("/company").then(r => setCompany(r.data)).catch(()=>{});
  };

  useEffect(() => { if (user?.role === "super_admin") fetchAll(); }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-5 py-20">Loading...</div>;
  if (!user || user.role !== "super_admin") {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center">
        <h1 className="font-display font-black text-4xl">Super Admin only</h1>
        <p className="text-[#484848] mt-2">Use the super@hostro.com account.</p>
        <Link to="/login" className="brut-btn mt-6 inline-flex">Sign in</Link>
      </div>
    );
  }

  const tabs = [
    { id: "staff", label: "Staff", icon: <Users size={14}/> },
    { id: "vendors", label: "Vendor KYC", icon: <ShieldCheck size={14}/> },
    { id: "company", label: "Site Settings", icon: <Settings size={14}/> },
    { id: "whatsapp", label: "WhatsApp", icon: <MessageCircle size={14}/> },
    { id: "email", label: "Email", icon: <Mail size={14}/> },
    { id: "audit", label: "Audit Log", icon: <ScrollText size={14}/> },
  ];

  return (
    <div className="panel-shell" data-testid="super-admin-shell">
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-10" data-testid="super-admin-page">
      <div className="brut-card p-6 bg-[#0F1F17] text-[#FDFCF8]">
        <div className="flex items-center gap-2"><Crown size={20}/><span className="brut-tag lime">SUPER ADMIN</span></div>
        <h1 className="font-display font-black text-3xl md:text-4xl mt-2">Hostro Mission Control</h1>
        <p className="text-[#bdbdbd] mt-2 text-sm">Manage staff, departments, site copy, WhatsApp logs and audit trail.</p>
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        {tabs.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} className={`brut-tag ${tab===t.id?"lime":"white"} cursor-pointer flex items-center gap-1`} data-testid={`sa-tab-${t.id}`}>{t.icon}{t.label}</button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "staff" && <StaffPanel staff={staff} refresh={fetchAll} showCreate={showCreate} setShowCreate={setShowCreate}/>}
        {tab === "vendors" && <VendorKycPanel vendors={vendors} refresh={fetchAll}/>}
        {tab === "company" && <SiteSettingsPanel company={company} refresh={fetchAll}/>}
        {tab === "whatsapp" && <WhatsAppPanel logs={waLogs} refresh={fetchAll}/>}
        {tab === "email" && <EmailPanel logs={emailLogs} refresh={fetchAll}/>}
        {tab === "audit" && <AuditPanel audit={audit}/>}
      </div>
    </div>
    </div>
  );
}

function StaffPanel({ staff, refresh, showCreate, setShowCreate }) {
  const [filter, setFilter] = useState("");
  const filtered = filter ? staff.filter(s => s.department === filter) : staff;
  const remove = async (sid) => {
    if (!confirm("Remove this staff member?")) return;
    await api.delete(`/admin/staff/${sid}`).catch(()=>toast.error("Failed"));
    toast.success("Staff removed");
    refresh();
  };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          <button onClick={()=>setFilter("")} className={`brut-tag ${filter===""?"lime":"white"} cursor-pointer`} data-testid="filter-all">All ({staff.length})</button>
          {DEPARTMENTS.map(d => (
            <button key={d.v} onClick={()=>setFilter(d.v)} className={`brut-tag ${filter===d.v?d.color:"white"} cursor-pointer`} data-testid={`filter-${d.v}`}>
              {d.label} ({staff.filter(s=>s.department===d.v).length})
            </button>
          ))}
        </div>
        <button onClick={()=>setShowCreate(true)} className="brut-btn" data-testid="create-staff-btn"><UserPlus size={14}/> Create staff</button>
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#ECFAF1] border-b-2 border-[#0F1F17]">
            <tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Department</th><th className="p-3 text-left">Sub-role</th><th className="p-3 text-left">Permissions</th><th className="p-3 text-right">Action</th></tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id} className="border-b border-[#0F1F17]/20" data-testid={`staff-${s.id}`}>
                <td className="p-3 font-medium">{s.name}</td>
                <td className="p-3 text-[#484848]">{s.email}</td>
                <td className="p-3"><span className="brut-tag lime !text-[10px]">{s.department}</span></td>
                <td className="p-3">{s.sub_role || <span className="text-[#484848]">—</span>}</td>
                <td className="p-3 text-xs">{(s.permissions || []).join(", ") || <span className="text-[#484848]">default</span>}</td>
                <td className="p-3 text-right">
                  <button className="brut-btn pink !py-1 !px-2 !text-xs" onClick={()=>remove(s.id)} data-testid={`remove-staff-${s.id}`}><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#484848]">No staff in this department.</td></tr>}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateStaffModal onClose={()=>setShowCreate(false)} onDone={()=>{setShowCreate(false); refresh();}}/>}
    </div>
  );
}

function CreateStaffModal({ onClose, onDone }) {
  const [f, setF] = useState({ name:"", email:"", password:"Staff@2025", phone:"", department:"finance", sub_role:"", permissions:"" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/staff", {
        ...f, sub_role: f.sub_role || null,
        permissions: f.permissions.split(",").map(s=>s.trim()).filter(Boolean),
      });
      toast.success(`Staff "${f.name}" created`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form className="brut-card p-6 max-w-lg w-full bg-[#FDFCF8] space-y-3" onClick={e=>e.stopPropagation()} onSubmit={submit} data-testid="create-staff-modal">
        <h3 className="font-display font-black text-2xl">Create staff member</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><label className="label-eyebrow">Name</label><input required className="brut-input mt-1" value={f.name} onChange={e=>setF({...f, name:e.target.value})} data-testid="staff-name"/></div>
          <div><label className="label-eyebrow">Email</label><input required type="email" className="brut-input mt-1" value={f.email} onChange={e=>setF({...f, email:e.target.value})} data-testid="staff-email"/></div>
          <div><label className="label-eyebrow">Phone</label><input className="brut-input mt-1" value={f.phone} onChange={e=>setF({...f, phone:e.target.value})}/></div>
          <div><label className="label-eyebrow">Password</label><input required minLength={6} className="brut-input mt-1" value={f.password} onChange={e=>setF({...f, password:e.target.value})}/></div>
          <div><label className="label-eyebrow">Department</label>
            <select className="brut-input mt-1" value={f.department} onChange={e=>setF({...f, department:e.target.value, sub_role:""})} data-testid="staff-department">
              {DEPARTMENTS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
            </select>
          </div>
          {f.department === "ground_staff" && (
            <div><label className="label-eyebrow">Sub-role</label>
              <select className="brut-input mt-1" value={f.sub_role} onChange={e=>setF({...f, sub_role:e.target.value})}>
                <option value="">— select —</option>
                {GROUND_SUB.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
        </div>
        <div><label className="label-eyebrow">Permissions (comma-separated)</label>
          <input className="brut-input mt-1" value={f.permissions} onChange={e=>setF({...f, permissions:e.target.value})} placeholder="view_bookings, approve_refunds"/>
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" className="brut-btn white flex-1" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="brut-btn flex-1" disabled={busy} data-testid="staff-submit">{busy?"Creating...":"Create"}</button>
        </div>
      </form>
    </div>
  );
}

function SiteSettingsPanel({ company, refresh }) {
  const [draft, setDraft] = useState(null);
  useEffect(()=>{ if (company) setDraft(JSON.stringify(company, null, 2)); }, [company]);
  const save = async () => {
    try {
      const parsed = JSON.parse(draft);
      await api.put("/admin/company", { settings: parsed });
      toast.success("Company info updated");
      refresh();
    } catch (e) {
      toast.error(e.message?.startsWith("Unexpected") ? "Invalid JSON" : formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };
  return (
    <div className="brut-card p-5 max-w-3xl">
      <h3 className="font-display font-black text-2xl mb-2">Editable site copy</h3>
      <p className="text-sm text-[#484848] mb-3">Edit company name, tagline, mission, vision, contact info, social links, logo URL. Changes go live immediately.</p>
      <textarea
        className="brut-input min-h-[400px] font-mono text-xs"
        value={draft || ""}
        onChange={e=>setDraft(e.target.value)}
        spellCheck={false}
        data-testid="settings-json"
      />
      <button className="brut-btn mt-3" onClick={save} data-testid="settings-save">Save site settings</button>
    </div>
  );
}

function WhatsAppPanel({ logs, refresh }) {
  const [f, setF] = useState({ to: "+91", template: "custom", message: "Hi from Hostro!" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/send", f);
      if (data.ok) toast.success(`Sent! SID: ${data.sid?.slice(-8)}`);
      else toast.error(`Failed: ${data.error}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="brut-card p-5" data-testid="whatsapp-send">
        <h3 className="font-display font-black text-2xl">Send WhatsApp</h3>
        <p className="text-xs text-[#484848] mt-1 bg-[#FFEAF6] p-2 brut-border">
          <strong>Twilio Sandbox:</strong> recipient must first send <code>join &lt;sandbox-keyword&gt;</code> to <strong>+1 415 523 8886</strong>. Find your keyword at console.twilio.com → Messaging → WhatsApp sandbox.
        </p>
        <label className="label-eyebrow mt-3 block">To (E.164)</label>
        <input className="brut-input mt-1" value={f.to} onChange={e=>setF({...f, to:e.target.value})} data-testid="wa-to"/>
        <label className="label-eyebrow mt-3 block">Template</label>
        <select className="brut-input mt-1" value={f.template} onChange={e=>setF({...f, template:e.target.value})} data-testid="wa-template">
          <option value="custom">Custom message</option>
          <option value="booking_confirmed">Booking confirmed</option>
          <option value="payment_reminder">Payment reminder</option>
          <option value="agreement_link">Agreement link</option>
        </select>
        {f.template === "custom" && (
          <>
            <label className="label-eyebrow mt-3 block">Message</label>
            <textarea className="brut-input mt-1 min-h-[80px]" value={f.message} onChange={e=>setF({...f, message:e.target.value})} data-testid="wa-message"/>
          </>
        )}
        <button className="brut-btn mt-4 w-full" onClick={send} disabled={busy} data-testid="wa-send-btn"><Send size={14}/> {busy?"Sending...":"Send WhatsApp"}</button>
      </div>
      <div>
        <h3 className="font-display font-black text-2xl mb-2">Recent messages ({logs.length})</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {logs.map(l => (
            <div key={l.id} className="brut-card p-3 text-sm">
              <div className="flex justify-between flex-wrap gap-2">
                <strong>{l.to}</strong>
                <span className={`brut-tag ${l.result?.ok?"lime":"pink"} !text-[10px]`}>{l.result?.ok ? "sent" : "failed"}</span>
              </div>
              <div className="text-xs text-[#484848] mt-1">{l.template} · {l.created_at?.slice(0,16)}</div>
              <p className="mt-1 text-xs">{l.body?.slice(0,120)}{l.body?.length>120?"...":""}</p>
              {!l.result?.ok && <div className="text-xs text-[#FF90E8] mt-1">{l.result?.error}</div>}
            </div>
          ))}
          {logs.length === 0 && <div className="brut-card p-6 text-center text-[#484848]">No messages yet.</div>}
        </div>
      </div>
    </div>
  );
}

function VendorKycPanel({ vendors, refresh }) {
  const [filter, setFilter] = useState("pending");
  const counts = { pending: 0, approved: 0, rejected: 0 };
  vendors.forEach(v => { counts[v.kyc_status] = (counts[v.kyc_status] || 0) + 1; });
  const filtered = filter === "all" ? vendors : vendors.filter(v => v.kyc_status === filter);

  const updateKyc = async (vid, status) => {
    let reason = "";
    if (status === "rejected") {
      reason = prompt("Rejection reason (will be sent to vendor):", "Insufficient documentation");
      if (reason === null) return;
    }
    try {
      await api.patch(`/admin/vendors/${vid}/kyc`, { status, reason });
      toast.success(`Vendor ${status}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div data-testid="vendor-kyc-panel">
      <div className="flex flex-wrap gap-2 mb-4">
        {["pending", "approved", "rejected", "all"].map(s => (
          <button key={s} onClick={()=>setFilter(s)} className={`brut-tag cursor-pointer ${filter === s ? "lime" : "white"}`} data-testid={`kyc-filter-${s}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)} {s !== "all" ? `(${counts[s] || 0})` : `(${vendors.length})`}
          </button>
        ))}
      </div>
      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#ECFAF1] border-b-2 border-[#0F1F17]">
            <tr><th className="p-3 text-left">Business</th><th className="p-3 text-left">Category</th><th className="p-3 text-left">City</th><th className="p-3 text-left">Contact</th><th className="p-3 text-left">Status</th><th className="p-3 text-right">Action</th></tr>
          </thead>
          <tbody>
            {filtered.map(v => (
              <tr key={v.id} className="border-b border-[#0F1F17]/20" data-testid={`kyc-row-${v.id}`}>
                <td className="p-3"><div className="font-bold">{v.business_name}</div><div className="text-xs text-[#484848]">{v.name}</div></td>
                <td className="p-3"><span className="brut-tag lime !text-[10px]">{v.category}</span></td>
                <td className="p-3 text-xs">{v.city}{v.pincode ? ` · ${v.pincode}` : ""}</td>
                <td className="p-3 text-xs"><div>{v.email}</div><div className="text-[#484848]">{v.phone}</div></td>
                <td className="p-3"><span className={`brut-tag ${v.kyc_status === "approved" ? "lime" : v.kyc_status === "rejected" ? "pink" : "white"} !text-[10px]`}>{v.kyc_status}</span></td>
                <td className="p-3 text-right space-x-1">
                  <Link to={`/vendors/${v.id}`} target="_blank" className="brut-btn white !py-1 !px-2 !text-xs">View</Link>
                  {v.kyc_status !== "approved" && <button className="brut-btn !py-1 !px-2 !text-xs" onClick={()=>updateKyc(v.id, "approved")} data-testid={`kyc-approve-${v.id}`}>Approve</button>}
                  {v.kyc_status !== "rejected" && <button className="brut-btn pink !py-1 !px-2 !text-xs" onClick={()=>updateKyc(v.id, "rejected")} data-testid={`kyc-reject-${v.id}`}>Reject</button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#484848]">No vendors in this state.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#484848] mt-3">⚠️ Only <strong>approved</strong> vendors appear in the public marketplace at <code>/vendors</code>.</p>
    </div>
  );
}

function EmailPanel({ logs, refresh }) {
  const [f, setF] = useState({ to: "", subject: "Hostro test email", message: "Hello from Hostro!" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email-test", f);
      if (data.ok) toast.success(data.mocked ? "Email sent (mock mode — no API key)" : `Sent! id: ${data.id?.slice(-8)}`);
      else toast.error(`Failed: ${data.error}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="brut-card p-5" data-testid="email-send">
        <h3 className="font-display font-black text-2xl">Send test email</h3>
        <p className="text-xs text-[#484848] mt-1 bg-[#FFEAF6] p-2 brut-border">
          <strong>Resend setup:</strong> add <code>RESEND_API_KEY</code> to <code>/app/backend/.env</code>. Without it, emails go to <em>mock log mode</em> (stored in DB, never delivered). Get a key from <code>resend.com/api-keys</code>.
        </p>
        <label className="label-eyebrow mt-3 block">To</label>
        <input type="email" className="brut-input mt-1" value={f.to} onChange={e=>setF({...f, to:e.target.value})} data-testid="email-to"/>
        <label className="label-eyebrow mt-3 block">Subject</label>
        <input className="brut-input mt-1" value={f.subject} onChange={e=>setF({...f, subject:e.target.value})}/>
        <label className="label-eyebrow mt-3 block">Message (plain text — wrapped in branded template)</label>
        <textarea className="brut-input mt-1 min-h-[80px]" value={f.message} onChange={e=>setF({...f, message:e.target.value})}/>
        <button className="brut-btn mt-4 w-full" onClick={send} disabled={busy || !f.to} data-testid="email-send-btn"><Mail size={14}/> {busy ? "Sending..." : "Send"}</button>
      </div>
      <div>
        <h3 className="font-display font-black text-2xl mb-2">Recent emails ({logs.length})</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {logs.map(l => (
            <div key={l.id} className="brut-card p-3 text-sm">
              <div className="flex justify-between flex-wrap gap-2">
                <strong>{l.to}</strong>
                <span className={`brut-tag ${l.status === "sent" ? "lime" : l.status === "mocked" ? "white" : "pink"} !text-[10px]`}>{l.status}</span>
              </div>
              <div className="text-xs text-[#484848] mt-1">{l.created_at?.slice(0, 16)}</div>
              <div className="font-medium mt-1">{l.subject}</div>
              {l.error && <div className="text-xs text-[#FF90E8] mt-1">{l.error}</div>}
            </div>
          ))}
          {logs.length === 0 && <div className="brut-card p-6 text-center text-[#484848]">No emails yet.</div>}
        </div>
      </div>
    </div>
  );
}

function AuditPanel({ audit }) {
  return (
    <div className="brut-card overflow-x-auto" data-testid="audit-table">
      <table className="w-full text-sm">
        <thead className="bg-[#ECFAF1] border-b-2 border-[#0F1F17]">
          <tr><th className="p-3 text-left">When</th><th className="p-3 text-left">Actor</th><th className="p-3 text-left">Role</th><th className="p-3 text-left">Action</th><th className="p-3 text-left">Entity</th><th className="p-3 text-left">Meta</th></tr>
        </thead>
        <tbody>
          {audit.map(a => (
            <tr key={a.id} className="border-b border-[#0F1F17]/20">
              <td className="p-3 text-xs">{a.created_at?.slice(0,19).replace("T"," ")}</td>
              <td className="p-3">{a.actor_email}</td>
              <td className="p-3"><span className="brut-tag white !text-[10px]">{a.actor_role}{a.actor_department?` · ${a.actor_department}`:""}</span></td>
              <td className="p-3"><strong>{a.action}</strong></td>
              <td className="p-3 text-xs">{a.entity}{a.entity_id?` (${a.entity_id.slice(-6)})`:""}</td>
              <td className="p-3 text-xs">{JSON.stringify(a.meta).slice(0,80)}</td>
            </tr>
          ))}
          {audit.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#484848]">No audit entries yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
